import { inject, Injectable } from '@angular/core';

import download from 'downloadjs';
import { Workbook, type Worksheet } from 'exceljs';
import { from, map, type Observable } from 'rxjs';

import { APP_CONFIG } from '@core/common-legacy';
import { injectTranslate } from '@core/i18n-legacy';
import type {
  SkillsIdeateResultJson,
  SkillsMatrixExportData,
  SkillsMatrixExportGroup,
  SkillsMatrixExportSkill,
} from '@core/tellent-skills-types';
import type { TellentAppCommonConfig } from '@core/tellent-types';
import { SupportedThemes } from '@core/theme-util';

const FONT = 'Arial';
const HYPERLINK_FONT = 'Calibri';

// Mirrors the reports PDF exporter (PdfExportTheme): the file always renders in
// the light theme.
const XlsxExportTheme = SupportedThemes.light;

// exceljs expects colors as ARGB (opaque alpha + RRGGBB); design tokens are
// #RRGGBB.
const argb = (hex: string): string => `FF${hex.replace('#', '').toUpperCase()}`;

const COLOR = {
  // Pure black has no semantic token — the DS default text token is steel, not
  // black.
  black: argb(XlsxExportTheme.black),
  headerBand: argb(XlsxExportTheme.colorBgPrimaryStrongDefault),
  skillName: argb(XlsxExportTheme.colorBgOfferDefault),
  positions: argb(XlsxExportTheme.colorBgPrimaryLowDefault),
  proficiency: argb(XlsxExportTheme.colorBgNeutralDefault),
  labelText: argb(XlsxExportTheme.colorTextDefault),
  positionsText: argb(XlsxExportTheme.colorTextPrimaryDefault),
  hyperlinkText: argb(XlsxExportTheme.colorTextDisabled),
  white: argb(XlsxExportTheme.colorTextOnprimaryDefault),
};

interface MatrixLabels {
  skill: string;
  description: string;
  category: string;
  positions: string;
  proficiencyLevels: string;
  expectations: string;
}

const LABEL_COLUMN_WIDTH = 28;
const DATA_COLUMN_WIDTH = 40;
const GROUP_ROW_HEIGHT = 35;
// Rows 1-2 are the header band and row 3 is left blank, so skills start at row 4.
const SKILLS_START_ROW = 4;
const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const SHEET_NAME_MAX_LENGTH = 31;

@Injectable({ providedIn: 'root' })
export class SkillsMatrixXlsxService {
  private readonly translate = injectTranslate();
  private readonly appConfig = inject<TellentAppCommonConfig>(APP_CONFIG);

  public toSkillsMatrixExportData(
    json: SkillsIdeateResultJson,
    title: string,
  ): SkillsMatrixExportData {
    return {
      title,
      groups: json.tabs.map(tab => ({
        name: tab.name,
        skills: tab.skills.map(skill => ({
          name: skill.name,
          description: skill.description,
          category: skill.category,
          proficiencyLevels: skill.proficiencies.map(p => p.name),
          expectations: skill.proficiencies.map(p => p.description),
          positions: skill.proficiencies.map(p => p.positions.join(', ')),
        })),
      })),
    };
  }

  public countSkills(json: SkillsIdeateResultJson): number {
    return json.tabs.reduce((total, tab) => total + tab.skills.length, 0);
  }

  public download(json: SkillsIdeateResultJson): Observable<void> {
    const data = this.toSkillsMatrixExportData(
      json,
      this.translate('tlc.positions.skills_ideate_flow.xlsx.title'),
    );
    // Content labels come from the backend (single source of truth); the two
    // structural row labels the backend does not send stay as frontend copy.
    const labels: MatrixLabels = {
      skill: json.skillLabel,
      description: json.descriptionLabel,
      category: json.categoryLabel,
      expectations: json.expectationsLabel,
      positions: this.translate('tlc.positions.skills_ideate_flow.xlsx.labels.positions'),
      proficiencyLevels: this.translate(
        'tlc.positions.skills_ideate_flow.xlsx.labels.proficiency_levels',
      ),
    };
    const workbook = new Workbook();
    workbook.title = data.title;

    const usedSheetNames = new Set<string>();
    data.groups.forEach(group =>
      this.buildWorksheet(workbook, group, labels, data.title, usedSheetNames),
    );

    const fileName = `${this.translate('tlc.positions.skills_ideate_flow.xlsx.file_name')}.xlsx`;

    return from(workbook.xlsx.writeBuffer()).pipe(
      map(buffer => {
        download(new Blob([buffer], { type: XLSX_MIME_TYPE }), fileName, XLSX_MIME_TYPE);
      }),
    );
  }

  private buildWorksheet(
    workbook: Workbook,
    group: SkillsMatrixExportGroup,
    labels: MatrixLabels,
    title: string,
    usedSheetNames: Set<string>,
  ): void {
    const worksheet = workbook.addWorksheet(this.sheetName(group.name, usedSheetNames));
    const maxDataColumns = Math.max(1, ...group.skills.map(s => s.proficiencyLevels.length));
    const lastColumn = 1 + maxDataColumns;

    worksheet.getColumn(1).width = LABEL_COLUMN_WIDTH;
    for (let column = 2; column <= lastColumn; column++) {
      worksheet.getColumn(column).width = DATA_COLUMN_WIDTH;
    }

    this.writeHeaderBand(worksheet, lastColumn, group.name, title);

    let row = SKILLS_START_ROW;
    group.skills.forEach(skill => {
      row = this.writeSkillBlock(worksheet, row, skill, labels, lastColumn);
      row += 1;
    });
  }

  private writeHeaderBand(
    worksheet: Worksheet,
    lastColumn: number,
    groupName: string,
    title: string,
  ): void {
    for (let column = 1; column <= lastColumn; column++) {
      worksheet.getCell(1, column).fill = this.solid(COLOR.headerBand);
      worksheet.getCell(2, column).fill = this.solid(COLOR.headerBand);
    }

    const titleCell = worksheet.getCell(1, 1);
    titleCell.value = title;
    titleCell.font = { name: FONT, size: 10, bold: true, color: { argb: COLOR.white } };
    titleCell.alignment = { vertical: 'top' };

    const linkCell = worksheet.getCell(1, lastColumn);
    const websiteUrl = this.appConfig.tellentWebsiteUrl;
    linkCell.value = {
      text: this.translate('tlc.positions.skills_ideate_flow.xlsx.hyperlink'),
      hyperlink: websiteUrl,
      tooltip: websiteUrl,
    };
    linkCell.font = {
      name: HYPERLINK_FONT,
      size: 10,
      underline: true,
      color: { argb: COLOR.hyperlinkText },
    };
    linkCell.alignment = { horizontal: 'right', vertical: 'top' };

    const groupCell = worksheet.getCell(2, 1);
    groupCell.value = groupName;
    groupCell.font = { name: FONT, size: 20, bold: true, color: { argb: COLOR.white } };
    groupCell.alignment = { vertical: 'top' };
    worksheet.getRow(2).height = GROUP_ROW_HEIGHT;
  }

  private writeSkillBlock(
    worksheet: Worksheet,
    startRow: number,
    skill: SkillsMatrixExportSkill,
    labels: MatrixLabels,
    lastColumn: number,
  ): number {
    let row = startRow;

    for (let column = 1; column <= lastColumn; column++) {
      worksheet.getCell(row, column).fill = this.solid(COLOR.skillName);
    }
    this.writeLabel(worksheet, row, labels.skill, COLOR.white);
    const nameCell = worksheet.getCell(row, 2);
    nameCell.value = skill.name;
    nameCell.font = { name: FONT, size: 13, bold: true, color: { argb: COLOR.white } };
    nameCell.alignment = { vertical: 'top' };
    row++;

    this.writeLabel(worksheet, row, labels.description);
    const descriptionCell = worksheet.getCell(row, 2);
    descriptionCell.value = skill.description;
    descriptionCell.font = { name: FONT, size: 10, color: { argb: COLOR.black } };
    descriptionCell.alignment = { vertical: 'top', wrapText: true };
    row++;

    this.writeLabel(worksheet, row, labels.category);
    const categoryCell = worksheet.getCell(row, 2);
    categoryCell.value = skill.category;
    categoryCell.font = { name: FONT, size: 10, color: { argb: COLOR.black } };
    categoryCell.alignment = { vertical: 'top' };
    row++;

    this.writeLabel(worksheet, row, labels.positions);
    skill.positions.forEach((position, index) => {
      const cell = worksheet.getCell(row, 2 + index);
      cell.value = position;
      cell.fill = this.solid(COLOR.positions);
      cell.font = { name: FONT, size: 10, bold: true, color: { argb: COLOR.positionsText } };
      cell.alignment = { horizontal: 'center', vertical: 'top', wrapText: true };
    });
    row++;

    this.writeLabel(worksheet, row, labels.proficiencyLevels);
    skill.proficiencyLevels.forEach((level, index) => {
      const cell = worksheet.getCell(row, 2 + index);
      cell.value = level;
      cell.fill = this.solid(COLOR.proficiency);
      cell.font = { name: FONT, size: 10, bold: true, color: { argb: COLOR.black } };
      cell.alignment = { horizontal: 'center', vertical: 'top' };
    });
    row++;

    this.writeLabel(worksheet, row, labels.expectations);
    skill.expectations.forEach((expectation, index) => {
      const cell = worksheet.getCell(row, 2 + index);
      cell.value = expectation;
      cell.fill = this.solid(COLOR.proficiency);
      cell.font = { name: FONT, size: 10, color: { argb: COLOR.black } };
      cell.alignment = { horizontal: 'left', vertical: 'top', wrapText: true };
    });
    row++;

    return row;
  }

  private writeLabel(
    worksheet: Worksheet,
    row: number,
    text: string,
    color = COLOR.labelText,
  ): void {
    const cell = worksheet.getCell(row, 1);
    cell.value = text;
    cell.font = { name: FONT, size: 10, color: { argb: color } };
    cell.alignment = { horizontal: 'right', vertical: 'top' };
  }

  private solid(argb: string) {
    return <const>{ type: 'pattern', pattern: 'solid', fgColor: { argb } };
  }

  private sheetName(name: string, usedNames: Set<string>): string {
    const base = this.sanitizeSheetName(name);

    let candidate = base;
    let counter = 1;
    // exceljs throws on an empty, duplicate (case-insensitive) or reserved sheet
    // name, which would abort the whole download. Suffix collisions until unique,
    // keeping the result within the 31-char cap.
    while (usedNames.has(candidate.toLowerCase()) || candidate.toLowerCase() === 'history') {
      counter += 1;
      const suffix = ` (${counter})`;
      candidate = `${base.slice(0, SHEET_NAME_MAX_LENGTH - suffix.length).trim()}${suffix}`;
    }

    usedNames.add(candidate.toLowerCase());
    return candidate;
  }

  private sanitizeSheetName(name: string): string {
    // Excel sheet names are capped at 31 chars, cannot contain : \ / ? * [ ] and
    // cannot start or end with a single quote.
    const cleaned = name
      .replace(/[:\\/?*[\]]/g, ' ')
      .replace(/^'+|'+$/g, '')
      .trim()
      .slice(0, SHEET_NAME_MAX_LENGTH)
      .trim();

    return cleaned || this.translate('tlc.positions.skills_ideate_flow.xlsx.sheet_fallback');
  }
}
